import {
  gameTimeZone,
  isGameTimeTbd,
} from '@sportninja/common/features/game/gameListView/gameDayBucket'
import {
  deriveGameStatus,
  hasMeaningfulScore,
} from '@sportninja/common/features/game/gameListView/gameStatus'
import type { GameStatusInfo } from '@sportninja/common/features/game/gameListView/gameStatus'
import { assignmentsForBucket } from '@sportninja/common/features/game/gameListView/officialBuckets'
import type { OfficialAssignment } from '@sportninja/common/features/game/gameListView/officialBuckets'
import type { GameLike } from '@sportninja/common/features/game/gameListView/types'
import { toVenueLocalParts } from '@sportninja/common/features/game/gameListView/venueLocalDateTime'

import type { RowResult } from '../hooks/useGameBulkEdit'
import { resolveVisitingTeamDisplay } from '@sportninja/common/features/game/placeholderVisitor/resolveVisitingTeamDisplay'

/**
 * What the Time cell HOLDS for a game whose start is not set yet.
 *
 * Not the label — the column's `valueFormatter` renders the localized
 * `gameGridTbd` ("À déterminer" in French), and this stays an ASCII constant so
 * the value never changes with the locale.
 *
 * The cell holds it rather than a blank so the value round-trips: the editor is
 * seeded with exactly what it commits back, which is the contract every other
 * column here keeps. It also makes re-asserting TBD on an already-TBD row a
 * genuine no-op — AG Grid fires `cellValueChanged` only when the value actually
 * differs, so a blank would dispatch a pointless action instead.
 */
export const TBD_CELL_VALUE = 'TBD'

export interface GameGridRow {
  id: string
  game: GameLike
  /** Canonical `YYYY-MM-DD` in the venue's zone — the sort key, not the label. */
  localDate: string
  /** Canonical `HH:mm` in the venue's zone, or `TBD_CELL_VALUE` when TBD. */
  localTime: string
  isTbd: boolean
  timeZone: string
  offsetName: string
  homeLabel: string
  awayLabel: string
  /**
   * The team uids the Home / Visitor cells hold and commit.
   *
   * The columns store IDs and render the labels beside them, for the same
   * reason every other option column does: team names genuinely repeat across
   * conferences, so a name-to-id lookup would silently write the wrong one.
   *
   * Null on a bracket-slot game, which has a slot rather than a team — the
   * label carries the slot's name and the formatter falls back to it.
   */
  homeTeamId: string | null
  visitingTeamId: string | null
  divisionLabel: string
  venueId: string | null
  facilityId: string | null
  gameNumber: string
  gameTypeId: number | null
  status: GameStatusInfo
  /** What the Status column sorts, filters and exports on. */
  statusLabel: string
  scoreLabel: string
  /**
   * The game's officials, split into the three columns that edit them.
   *
   * Each entry is a (person, role) PAIR: two referees are two people holding the
   * same role, and one person can hold a role in more than one bucket. The role
   * is the one for THIS game, not the one stored on the person.
   *
   * Names are deliberately absent — the columns resolve them through the options
   * map, which also covers officials the search pool does not return.
   */
  referees: OfficialAssignment[]
  scorekeepers: OfficialAssignment[]
  timekeepers: OfficialAssignment[]
  isPending: boolean
  movedOutsideFilter: boolean
  failure?: RowResult
}

/**
 * Per-side display: the real team if it has one, otherwise the placeholder slot
 * it was generated with, otherwise TBD.
 *
 * A slot-only game is still a real booking on real ice — it has to stay visible
 * and retimeable, so it must never render as a blank row.
 */
const sideLabel = (
  team: GameLike['homeTeam'],
  slot: GameLike['homeTeamSlot'],
  tbdLabel: string,
  //  Admin-typed visitor name, for a game with no team and no slot (SN-8410).
  placeholderName?: string | null
): string =>
  team?.name_full ??
  team?.name ??
  slot?.name_full ??
  slot?.name ??
  placeholderName ??
  tbdLabel

/**
 * The score, or why there isn't one.
 *
 * A bare blank was the reported ambiguity: "not played yet" and "played, nobody
 * entered it" looked identical. A game that is not expecting a score gets an
 * em dash — nothing is missing — while a final game with no score entered stays
 * blank, because there something IS missing and the Status column beside it
 * says which.
 *
 * The list resource only sends the score fields once a game has started or been
 * forfeited, so the cast is reading fields that are genuinely absent the rest of
 * the time rather than fields it forgot to declare.
 */
const scoreLabel = (game: GameLike, status: GameStatusInfo): string => {
  const record = game as unknown as Record<string, unknown>
  const home = record.home_team_score
  const away = record.visiting_team_score
  if (home == null || away == null) {
    return hasMeaningfulScore(status) ? '' : '—'
  }
  return `${home} - ${away}`
}

export interface BuildRowsContext {
  pendingIds: Set<string>
  retainedOutsideFilterIds: Set<string>
  failuresById: Map<string, RowResult>
  tbdLabel: string
  /**
   * Localizes a status for sorting, filtering and export.
   *
   * Injected rather than imported: the status DERIVATION is cross-platform, its
   * wording is not, and a row builder that reached for web's i18n would pin this
   * file to web.
   */
  statusLabel: (status: GameStatusInfo) => string
}

export const toGameGridRow = (
  game: GameLike,
  context: BuildRowsContext
): GameGridRow => {
  const timeZone = gameTimeZone(game)
  const isTbd = isGameTimeTbd(game)
  const parts = toVenueLocalParts(game.starts_at, timeZone)
  const status = deriveGameStatus(game)

  return {
    id: game.id,
    game,
    localDate: parts.date,
    localTime: isTbd ? TBD_CELL_VALUE : parts.time,
    isTbd,
    timeZone,
    offsetName: parts.offsetName,
    homeLabel: sideLabel(game.homeTeam, game.homeTeamSlot, context.tbdLabel),
    awayLabel: sideLabel(
      game.visitingTeam,
      game.visitingTeamSlot,
      context.tbdLabel,
      resolveVisitingTeamDisplay(game).name
    ),
    homeTeamId: game.homeTeam?.id ?? null,
    visitingTeamId: game.visitingTeam?.id ?? null,
    divisionLabel: game.schedule?.name_full ?? game.schedule?.name ?? '',
    venueId: game.venue?.id ?? null,
    facilityId: game.facility?.id ?? null,
    gameNumber: game.league_game_number ?? '',
    gameTypeId: game.game_type_id == null ? null : Number(game.game_type_id),
    status,
    statusLabel: context.statusLabel(status),
    scoreLabel: scoreLabel(game, status),
    referees: assignmentsForBucket(game, 'referees'),
    scorekeepers: assignmentsForBucket(game, 'scorekeepers'),
    timekeepers: assignmentsForBucket(game, 'timekeepers'),
    isPending: context.pendingIds.has(game.id),
    movedOutsideFilter: context.retainedOutsideFilterIds.has(game.id),
    failure: context.failuresById.get(game.id),
  }
}

export const buildGameGridRows = (
  games: GameLike[],
  context: BuildRowsContext
): GameGridRow[] => games.map((game) => toGameGridRow(game, context))

/** Blank/TBD times sort last in both directions, never interleaved. */
export const compareLocalTime = (a: string, b: string): number => {
  const aEmpty = a === '' || a === TBD_CELL_VALUE
  const bEmpty = b === '' || b === TBD_CELL_VALUE
  //  Explicit, not lexical luck: 'TBD' does happen to sort after '23:59', but
  //  a comparator that leaned on that would break the day someone renames the
  //  marker.
  if (aEmpty && bEmpty) {
    return 0
  }
  if (aEmpty) {
    return 1
  }
  if (bEmpty) {
    return -1
  }
  return a === b ? 0 : a < b ? -1 : 1
}

/** Numeric when both sides are numeric, lexical otherwise — "10" after "9". */
export const compareGameNumber = (a: string, b: string): number => {
  const left = Number(a)
  const right = Number(b)
  const bothNumeric =
    a !== '' && b !== '' && Number.isFinite(left) && Number.isFinite(right)

  if (bothNumeric) {
    return left - right
  }
  if (a === b) {
    return 0
  }
  if (a === '') {
    return 1
  }
  if (b === '') {
    return -1
  }
  return a < b ? -1 : 1
}
